import { FilepickerFile } from "@/components/ui-v2/FilePicker";
import {
  ReferenceImageType,
  ReferenceVideoType,
} from "@/components/ui-v2/References/types";
import { useSnackbar } from "@/components/ui-v2/Snackbar";
import { StyleCapabilities } from "@/types";
import { getImageObject } from "@/utils/images";

import { ActivateAsset, ActivateFile, ReferenceImage } from "./types";

export const isImageWithinInputDimension = (
  image: Pick<ReferenceImageType, "width" | "height">,
  boundaries?: StyleCapabilities["imageUpscaleMaxInputDimensionRange"],
) => {
  const { width, height } = image;

  if (width < 16 || height < 16) {
    return false;
  }

  if (!boundaries) {
    return true;
  }

  const [maxWidth, maxHeight] = boundaries;

  return width <= maxWidth && height <= maxHeight;
};

/**
 * Creates a ReferenceVideoType from a file if it has valid dimensions.
 * Returns undefined if width or height are missing.
 */
export const createVideoReference = (
  file: ActivateFile,
): ReferenceVideoType | undefined => {
  const width = file.metadata?.width;
  const height = file.metadata?.height;

  if (!width || !height) {
    return undefined;
  }

  return {
    width,
    height,
    src: file.url,
    driveFileId: file.id,
  };
};

export type ConvertToImageParams = {
  source: ActivateFile | ActivateAsset;
  showInfo?: ReturnType<typeof useSnackbar>["showInfo"];
  convertAssetSnapshotToFile: (
    source: ActivateAsset,
  ) => Promise<FilepickerFile | undefined>;
};

export const convertToRefImage = async (
  params: ConvertToImageParams,
): Promise<ReferenceImage | undefined> => {
  const { source, showInfo, convertAssetSnapshotToFile } = params;

  switch (source.__typename) {
    case "File": {
      const width = source.metadata?.width;
      const height = source.metadata?.height;
      if (!width || !height) {
        return undefined;
      }
      return {
        src: source.url,
        width,
        height,
        driveFileId: source.id,
      };
    }
    case "Asset": {
      if (!source.previewUrl) {
        return undefined;
      }

      showInfo?.("Processing image...", { messageId: "processing-image" });

      const [file, img] = await Promise.all([
        convertAssetSnapshotToFile(source),
        getImageObject(source.previewUrl),
      ]);

      if (!file || !img) {
        return undefined;
      }

      return {
        src: file.src,
        width: img.width,
        height: img.height,
        driveFileId: file.id,
      };
    }
    default:
      throw new Error(
        `Invalid source type: ${(source as any).__typename}. Expected "File" or "Asset".`,
      );
  }
};
